import React, { useMemo, useContext, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { ModalContext } from "components/ModalContext";
import UnassignFromCollection from "components/UnassignFromCollection";
import SaveAsCollection from "components/SaveAsCollection";
import DropDownAddCollection from "components/uiComponents/DropDownAddCollection";
import SaveCollection from "components/SaveCollection";
import UniversalButton from "components/uiComponents/UniversalButton";
import DownloadInfoTableExcel from "components/DownloadInfoTableExcel";
import SetDataModal from "components/modals/SetDataModal/SetDataModal";
import { tabType } from "utils/data";
import { useConditionModal } from "../model/use-condition-modal";
import { getTableDataForSelectedElements } from "../lib/get-table-data-for-selected-elements";
import { getFileName } from "../lib/get-file-name";
import { ReactComponent as FunctionIcon } from "static/icons/function.svg";
import { ReactComponent as OpenIcon } from "static/icons/open.svg";
import { ReactComponent as SetDataIcon } from "static/icons/set-data-icon.svg";
import DotMenu from "../../DotMenu";

const safeParseRowSelection = (modelId = null) => {
  try {
    const { getCurrentNamespace } = require('utils/crossTabSync');
    const currentNs = getCurrentNamespace() || modelId;
    const rowSelectionKey = currentNs ? `rowSelection:${currentNs}` : (modelId ? `rowSelection:${modelId}` : 'rowSelection');
    const raw = localStorage.getItem(rowSelectionKey);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const toIdsCount = (val) => {
  if (!val) return 0;
  if (Array.isArray(val)) return val.length;
  if (typeof val === "object") {
    return Object.keys(val).filter((k) => !!val[k]).length;
  }
  return 0;
};

const InfoTableActions = ({
  table,
  selectedItems,
  selectedRows,
  handleClearSelection,
  columns,
  filteredRows,
  rows,
  openActiveTabInNewTab,
  hideWrapperInner,
  modelId = null,
}) => {
  const { t } = useTranslation();
  const { handleModal } = useContext(ModalContext);

  const onShowConditionModal = useConditionModal({
    tableId: table.id,
  });

  const handleCloseModal = () => handleModal(null, true);

  const setDataModalHandler = () => {
    handleModal(
      <SetDataModal
        table={table}
        columns={columns}
        selectedItems={selectedItems}
        closeModal={handleCloseModal}
        modelId={modelId}
      />,
      false,
      true
    );
  };

  const [storageSelection, setStorageSelection] = useState(() => safeParseRowSelection(modelId));

  // Reactively read rowSelection from localStorage (updates when external window closes)
  useEffect(() => {
    const onStorage = (e) => {
      if (e.storageArea !== localStorage) return;
      try {
        const { getCurrentNamespace } = require('utils/crossTabSync');
        const currentNs = getCurrentNamespace() || modelId;
        const rowSelectionKey = currentNs ? `rowSelection:${currentNs}` : (modelId ? `rowSelection:${modelId}` : 'rowSelection');
        if (e.key === rowSelectionKey || e.key === "newTabOpened") {
          try {
            setStorageSelection(safeParseRowSelection(modelId));
          } catch {}
        }
      } catch {}
    };
    window.addEventListener("storage", onStorage);
    // Also check on mount and when selectedRows/selectedItems change
    setStorageSelection(safeParseRowSelection(modelId));
    return () => window.removeEventListener("storage", onStorage);
  }, [selectedRows, selectedItems, modelId]);

  const isItemsSelected = useMemo(() => {
    const rowsSelected = Array.isArray(selectedRows) && selectedRows.length > 0;
    const itemsSelectedCount = toIdsCount(selectedItems);
    const storageSelectedCount = toIdsCount(storageSelection);

    if (!!hideWrapperInner) {
      return storageSelectedCount > 0
    } else {
      return rowsSelected || itemsSelectedCount > 0 || storageSelectedCount > 0;
    }
  }, [selectedRows, selectedItems, storageSelection]);

  const processedTableInfo = useMemo(
    () => getTableDataForSelectedElements(table, selectedItems),
    [selectedItems, table]
  );

  const fileName = useMemo(
    () => getFileName(table.folder, table.name),
    [table.folder, table.name]
  );

  // Get all rows from currentTableState to find matching data (fallback)
  const allRowsFromState = useSelector((state) => state.table.currentTableState.rows) || [];
  // Use rows prop if available, otherwise fallback to allRowsFromState
  const allRows = rows || allRowsFromState;

  // Helper function to convert row data to export format
  const convertRowToExportFormat = useMemo(() => {
    return (rowData, cols) => {
      // If rowData has 'lines' array, convert it using columns accessorKey
      if (rowData?.lines && Array.isArray(rowData.lines) && cols && Array.isArray(cols)) {
        const result = {};
        cols.forEach((column, index) => {
          const columnId = column.id || column.accessorKey;
          if (columnId && rowData.lines[index] !== undefined) {
            result[columnId] = rowData.lines[index];
          }
        });
        return result;
      }
      
      // Otherwise, use rowData as is (already in correct format)
      const result = {};
      if (cols && Array.isArray(cols)) {
        cols.forEach((column) => {
          const columnId = column.id || column.accessorKey;
          if (columnId) {
            result[columnId] = rowData?.[columnId] ?? rowData?.[column.accessorKey] ?? null;
          }
        });
      } else if (rowData && typeof rowData === 'object') {
        Object.keys(rowData).forEach((key) => {
          if (key !== 'lineElements' && key !== 'original' && key !== 'lines') {
            result[key] = rowData[key];
          }
        });
      }
      
      return result;
    };
  }, []);

  // Convert selectedRows (react-table format) to simple data objects for DownloadInfoTableExcel
  const selectedDataForExport = useMemo(() => {
    if (!isItemsSelected) {
      return [];
    }
    
    // If selectedRows is empty but we have storageSelection or selectedItems, find rows from allRows
    let rowsToProcess = selectedRows;
    if ((!selectedRows || selectedRows.length === 0) && allRows && allRows.length > 0) {
      // Try to get selection from storageSelection (for table window)
      const selectionKeys = Object.keys(storageSelection || {}).filter(key => storageSelection[key]);
      
      if (selectionKeys.length > 0) {
        // Find rows by matching rowId (format: le:elementId1|elementId2 or index)
        rowsToProcess = allRows.filter((row, index) => {
          const rowId = (() => {
            const ids = Array.isArray(row?.lineElements) ? row.lineElements : 
                       (Array.isArray(row?.original?.lineElements) ? row.original.lineElements : []);
            if (ids.length) return `le:${ids.join("|")}`;
            return String(index);
          })();
          return selectionKeys.includes(rowId);
        });
      } else if (selectedItems && (Array.isArray(selectedItems) ? selectedItems.length > 0 : Object.keys(selectedItems).length > 0)) {
        // Try to get from selectedItems
        const selectedItemsArray = Array.isArray(selectedItems) ? selectedItems : Object.keys(selectedItems);
        rowsToProcess = allRows.filter((row) => {
          const rowLineElements = row?.lineElements || row?.original?.lineElements || [];
          return rowLineElements.some(id => selectedItemsArray.includes(id));
        });
      }
    }
    
    if (!rowsToProcess || rowsToProcess.length === 0) {
      return [];
    }
    
    return rowsToProcess.map((row) => {
      const rowData = row?.original || row;
      return convertRowToExportFormat(rowData, columns);
    });
  }, [selectedRows, columns, isItemsSelected, allRows, rows, storageSelection, selectedItems, convertRowToExportFormat]);

  // Convert filteredRows to simple data objects
  // Use rows as fallback if filteredRows is empty (e.g., in table window)
  const filteredDataForExport = useMemo(() => {
    const sourceRows = (filteredRows && Array.isArray(filteredRows) && filteredRows.length > 0) 
      ? filteredRows 
      : (allRows && Array.isArray(allRows) && allRows.length > 0 ? allRows : []);
    
    if (!sourceRows || sourceRows.length === 0) {
      return [];
    }
    
    return sourceRows.map((row) => {
      const rowData = row?.original || row;
      return convertRowToExportFormat(rowData, columns);
    });
  }, [filteredRows, columns, allRows, rows, convertRowToExportFormat]);

  return (
    <>
      {columns && filteredDataForExport.length ? (
        <DownloadInfoTableExcel
          columns={columns}
          filteredData={filteredDataForExport}
          selectedData={selectedDataForExport}
          fileName={fileName}
          isChecked={isItemsSelected}
        />
      ) : null}

      {isItemsSelected ? (
        <>
          {table?.type !== "saved" && (
            <UniversalButton
              type="button"
              label={t("set_data")}
              leftIcon={<SetDataIcon />}
              size="md"
              isRounded="sm"
              labelColor="blue"
              labelHoverColor="blue"
              backgroundColor="blue"
              backgroundHoverColor="blue-light"
              onClick={setDataModalHandler}
            />
          )}

          <DotMenu>
            <UniversalButton
              type="button"
              label={t("add_function")}
              leftIcon={<FunctionIcon />}
              size="md"
              isRounded="sm"
              labelColor="blue"
              labelHoverColor="blue"
              backgroundColor="blue"
              backgroundHoverColor="blue-light"
              onClick={onShowConditionModal}
              unstyled
            />

            {!hideWrapperInner && (
              <UniversalButton
                type="button"
                label={t("open_table_in_new_tab")}
                leftIcon={<OpenIcon />}
                size="md"
                isRounded="sm"
                labelColor="blue"
                labelHoverColor="blue"
                backgroundColor="blue"
                backgroundHoverColor="blue-light"
                onClick={openActiveTabInNewTab}
                unstyled
              />
            )}

            {!hideWrapperInner && (
              <>
                {table.type !== tabType.report ? (
                  <UnassignFromCollection
                    deleteSelectElements={handleClearSelection}
                    tableInfo={processedTableInfo}
                    unstyled
                  />
                ) : null}

                <SaveAsCollection tableInfo={processedTableInfo} unstyled />
                <DropDownAddCollection tableInfo={processedTableInfo} unstyled />
              </>
            )}
          </DotMenu>
        </>
      ) : (
        <>
          {!hideWrapperInner && (
            <UniversalButton
              type="button"
              label={t("open_table_in_new_tab")}
              leftIcon={<OpenIcon />}
              size="md"
              isRounded="sm"
              labelColor="blue"
              labelHoverColor="blue"
              backgroundColor="blue"
              backgroundHoverColor="blue-light"
              onClick={openActiveTabInNewTab}
            />
          )}

          <UniversalButton
            type="button"
            label={t("add_function")}
            leftIcon={<FunctionIcon />}
            size="md"
            isRounded="sm"
            labelColor="blue"
            labelHoverColor="blue"
            backgroundColor="blue"
            backgroundHoverColor="blue-light"
            onClick={onShowConditionModal}
          />

          {table.type === tabType.unsaved && !hideWrapperInner ? (
            <SaveCollection tableInfo={table} />
          ) : null}
        </>
      )}
    </>
  );
};

export default InfoTableActions;